/**
 * Completion content for build-cocm-care-plan tasks.
 * Allows the user to select optional CoCM activities, set approximate dates,
 * and add them to the care plan on completion.
 * @agentic connections
 */

import { Stack, Text, Alert, Checkbox, Group, Paper } from "@mantine/core";
import { useForm } from "@mantine/form";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { IconAlertCircle } from "@tabler/icons-react";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  CarePlan,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import {
  getFlexibleActivitiesForType,
  getCarePlanActivityDisplay,
  getCarePlanActivityDescription,
} from "@benchmark/core/constants";
import { addCarePlanActivity } from "@benchmark/core/actions";
import { handleActivityCompletion } from "@benchmark/core/workflows";
import { getResourceIdFromReference } from "@benchmark/core/utils";
import { findBenchmarkPractitionerRole } from "@benchmark/core/services";
import { showSuccessNotification } from "@benchmark/ui/utils";
import { toDateInputValue } from "@benchmark/ui/utils";
import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import { NextTaskOwnerSection } from "@/components/shared/forms/NextTaskOwnerSection";
import { CareTimeActivityDurationField } from "@/components/domain/care-time/CareTimeActivityDurationField";
import { useTeeCareTimeForTask } from "@/hooks/domain/useTeeCareTimeForEncounter";
import type { CompletionContentProps } from "./DefaultCompletionContent";
import { CONFIG } from "@/app/config";

/**
 * State for a single selectable CoCM activity.
 * @agentic connections
 */
type SelectableActivity = {
  code: string;
  display: string;
  description: string;
  selected: boolean;
  targetDate: string;
};

/**
 * Completion form for build-cocm-care-plan: select activities, set dates,
 * then add them to the care plan and mark this task complete.
 * @agentic connections
 */
export const BuildCoCMCarePlanCompletionContent = ({
  task,
  onDone,
  onCancel,
  onDirtyChange,
  nextActivityLabel,
  nextActivityDefaultOwner,
  nextActivityAppliedRule,
  nextTaskOwnerOverride,
  onNextTaskOwnerOverrideChange,
  isResolvingNextTaskOwner = false,
}: CompletionContentProps) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [careMinutes, setCareMinutes] = useState<number | undefined>(undefined);
  const teeForTask = useTeeCareTimeForTask();
  const [practitionerRole, setPractitionerRole] = useState<
    PractitionerRole | undefined
  >(undefined);

  useEffect(() => {
    if (!profile?.id) return;
    findBenchmarkPractitionerRole(medplum, {
      reference: `Practitioner/${profile.id}`,
      benchmarkOrganizationId: CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    })
      .then(setPractitionerRole)
      .catch(() => setPractitionerRole(undefined));
  }, [medplum, profile?.id]);

  const flexibleActivities = useMemo(
    () => getFlexibleActivitiesForType("cocm"),
    [],
  );

  const initialActivities = useMemo(
    () =>
      flexibleActivities.map((a) => ({
        code: a.code,
        display: getCarePlanActivityDisplay(a.code),
        description: getCarePlanActivityDescription(a.code),
        selected: false,
        targetDate: "",
      })),
    [flexibleActivities],
  );

  const form = useForm<{ activities: SelectableActivity[] }>({
    initialValues: {
      activities: initialActivities,
    },
  });

  const activities = form.values.activities;
  const selectedCount = activities.filter((a) => a.selected).length;
  const today = toDateInputValue(new Date());
  const steps = useMemo(
    () => [
      {
        id: "details",
        title: "Activities",
        subtitle: "Select CoCM activities",
        validate: () =>
          selectedCount === 0
            ? { activities: "Select at least one activity" }
            : null,
      },
      {
        id: "assignment",
        title: "Assignment",
        subtitle: "Review next task assignment",
      },
    ],
    [selectedCount],
  );

  const toggleActivity = useCallback(
    (code: string) => {
      form.setFieldValue(
        "activities",
        activities.map((a) =>
          a.code === code ? { ...a, selected: !a.selected } : a,
        ),
      );
    },
    [form, activities],
  );

  const setTargetDate = useCallback(
    (code: string, date: string) => {
      form.setFieldValue(
        "activities",
        activities.map((a) =>
          a.code === code ? { ...a, targetDate: date } : a,
        ),
      );
    },
    [form, activities],
  );

  const handleSubmit = useCallback(async () => {
    if (!task || selectedCount === 0) return;

    setSubmitting(true);
    setError(null);

    try {
      const carePlanRef = task.basedOn?.find((ref) =>
        ref.reference?.startsWith("CarePlan/"),
      );
      if (!carePlanRef?.reference) {
        throw new Error("Task is not linked to a CarePlan");
      }
      const carePlanId = getResourceIdFromReference(
        carePlanRef.reference,
        "CarePlan",
      );
      if (!carePlanId) {
        throw new Error("Could not resolve CarePlan ID from task");
      }

      let carePlan: CarePlan = await medplum.readResource(
        "CarePlan",
        carePlanId,
      );

      const selected = activities.filter((a) => a.selected);

      const sorted = [...selected].sort((a, b) => {
        if (a.targetDate && b.targetDate)
          return a.targetDate.localeCompare(b.targetDate);
        if (a.targetDate && !b.targetDate) return -1;
        if (!a.targetDate && b.targetDate) return 1;
        return 0;
      });

      for (const activity of sorted) {
        carePlan = await addCarePlanActivity(medplum, {
          carePlan,
          activityCode: activity.code,
          targetDate: activity.targetDate || undefined,
        });
      }

      const selectedSummary = selected
        .map((a) => {
          const datePart = a.targetDate ? ` (target: ${a.targetDate})` : "";
          return `${a.display}${datePart}`;
        })
        .join(", ");
      const statusReason = `CoCM care plan built with activities: ${selectedSummary}`;

      const activityCode = task.code?.coding?.[0]?.code;
      if (activityCode) {
        await handleActivityCompletion(medplum, {
          carePlan,
          activityCode,
          statusReason,
          completionData: nextTaskOwnerOverride
            ? {
                nextActivityOwnerRef: nextTaskOwnerOverride.ref,
                nextActivityOwnerDisplay: nextTaskOwnerOverride.display,
              }
            : undefined,
          practitionerRole,
        });
      }

      await teeForTask({
        task,
        minutes: careMinutes,
        method: "task-completion",
      });

      showSuccessNotification("CoCM care plan activities added successfully");
      onDone();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to build care plan",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    task,
    activities,
    selectedCount,
    medplum,
    practitionerRole,
    onDone,
    nextTaskOwnerOverride,
    careMinutes,
    teeForTask,
  ]);

  useEffect(() => {
    onDirtyChange?.(
      selectedCount > 0 || !!nextTaskOwnerOverride || !!careMinutes,
    );
  }, [selectedCount, nextTaskOwnerOverride, careMinutes, onDirtyChange]);

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <SteppedForm
        form={form}
        steps={steps}
        onSubmit={() => handleSubmit()}
        isSubmitting={submitting}
        submitText="Build Care Plan"
        onCancel={onCancel}
      >
        <FormStep id="details">
          <Stack gap="md">
            <Text size="sm">
              Select the CoCM activities for this member&apos;s care plan and
              set approximate target dates.
            </Text>

            <Stack gap="sm">
              {activities.map((activity) => (
                <Paper
                  key={activity.code}
                  p="sm"
                  withBorder
                  style={{
                    borderColor: activity.selected
                      ? "var(--mantine-color-purple-4)"
                      : undefined,
                    backgroundColor: activity.selected
                      ? "var(--mantine-color-purple-0)"
                      : undefined,
                  }}
                >
                  <Group
                    justify="space-between"
                    align="flex-start"
                    wrap="nowrap"
                  >
                    <Checkbox
                      label={activity.display}
                      description={activity.description}
                      checked={activity.selected}
                      onChange={() => toggleActivity(activity.code)}
                      color="purple"
                      styles={{ body: { alignItems: "flex-start" } }}
                    />
                    {activity.selected && (
                      <TextInput
                        type="date"
                        size="sm"
                        placeholder="Target date"
                        value={activity.targetDate}
                        onChange={(e) =>
                          setTargetDate(activity.code, e.currentTarget.value)
                        }
                        min={today}
                        aria-label={`Target date for ${activity.display}`}
                      />
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>

            {selectedCount > 0 && (
              <Text size="xs" c="dimmed">
                {selectedCount}{" "}
                {selectedCount === 1 ? "activity" : "activities"} selected
              </Text>
            )}
          </Stack>
        </FormStep>

        <FormStep id="assignment">
          <Stack gap="md">
            <NextTaskOwnerSection
              nextActivityLabel={nextActivityLabel}
              defaultOwner={nextActivityDefaultOwner}
              appliedRule={nextActivityAppliedRule}
              overrideOwner={nextTaskOwnerOverride}
              onOverrideChange={onNextTaskOwnerOverrideChange ?? (() => {})}
              isResolving={isResolvingNextTaskOwner}
            />
            <CareTimeActivityDurationField
              minutes={careMinutes}
              onMinutesChange={setCareMinutes}
            />
          </Stack>
        </FormStep>
      </SteppedForm>
    </Stack>
  );
};
